import moment from "moment";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import type { BankAccount } from "@freightsimple/generated-dashboard-openapi-client/models/BankAccount";
import type { CreditCard } from "@freightsimple/generated-dashboard-openapi-client/models/CreditCard";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { Quote } from "@freightsimple/generated-dashboard-openapi-client/models/Quote";
import type { QuoteWithOriginalPrice } from "@/Shared/Coupons/Hooks/useCoupons";
import { isDomesticCanada } from "@/Helpers/isDomesticCanada";
import QuotesDeliveryColumn from "@/Shared/Quotes/CustomComponents/QuotesDeliveryColumn";
import QuotesPickupColumn from "@/Shared/Quotes/CustomComponents/QuotesPickupColumn";
import QuotesProgress from "@/Shared/Quotes/CustomComponents/QuotesProgress";
import QuotesTransitColumn from "@/Shared/Quotes/CustomComponents/QuotesTransitColumn";
import { QuotesColumnHeader } from "@/Shared/Quotes/CustomComponents/QuotesColumnHeader";
import { QuotesPrimaryText } from "@/Shared/Quotes/CustomComponents/QuotesPrimaryText";
import { BookShipmentReviewSummaryColumns } from "../CustomComponents/Internal/bookShipmentReviewSummaryColumns";

interface BookShipmentReviewSummaryPanelProps {
  shipment: PreBookingShipment;
  selectedQuote: Quote;
  discountedQuote: Quote;
  deliveryHours: string;
  selectedCard: CreditCard | undefined;
  selectedBankAccount: BankAccount | undefined;
  paymentTermsDays: number;
  onChangePaymentMethod: (() => void) | undefined;
  onChangeBroker: () => Promise<void>;
  onChangePickupDate: () => void;
  onModifyDetails: () => void;
  onModifyPickupAddress: () => void;
  onModifyDeliveryAddress: () => void;
  onSelectAnotherQuote: () => void;
}

/**
 * The pickup / transit / delivery summary of the shipment about to be booked —
 * the same three columns a quote is shown in, so what the customer confirms
 * here looks like what they picked.
 *
 * Every "modify" affordance is a prop rather than routing done here: several of
 * them go to the same screen, and which ones is the booking flow's business.
 */
export function BookShipmentReviewSummaryPanel(
  props: BookShipmentReviewSummaryPanelProps,
) {
  const {
    shipment,
    selectedQuote,
    discountedQuote,
    deliveryHours,
    selectedCard,
    selectedBankAccount,
    paymentTermsDays,
  } = props;

  return (
    <Panel collapseOnNarrow>
      <Stack gap={10}>
        {shipment.selectedQuote?.notes && (
          <Stack gap={0}>
            <QuotesPrimaryText bold>Notes</QuotesPrimaryText>
            <QuotesColumnHeader>
              {shipment.selectedQuote.notes}
            </QuotesColumnHeader>
          </Stack>
        )}
        <Stack gap={4}>
          <QuotesProgress shipmentState={shipment.shipmentState} />
          <BookShipmentReviewSummaryColumns>
            <QuotesPickupColumn
              pickupDeadline={shipment.pickupDeadline}
              lineItems={shipment.lineItems}
              pickupDate={moment(shipment.pickupDate)}
              pickupLocation={shipment.pickupLocation}
              pickupContact={shipment.pickupContact}
              shipmentState={shipment.shipmentState}
              quote={selectedQuote}
              pickupReferenceNumber={shipment.pickupReferenceNumber}
              onModifyDetails={props.onModifyDetails}
              onModifyPickupAddress={props.onModifyPickupAddress}
              onModifyPickupContact={props.onModifyPickupAddress}
              onModifyPickupReferenceAndNotes={props.onModifyPickupAddress}
              onChangePickupDate={props.onChangePickupDate}
              isDomesticCanada={isDomesticCanada(
                shipment.pickupLocation,
                shipment.deliveryLocation,
              )}
              showNotesSection={true}
              pickupBoothNumber={shipment.pickupBoothNumber}
              pickupLocationHasDock={shipment.pickupLocationHasDock}
              pickupLocationHasForklift={shipment.pickupLocationHasForklift}
            />
            <QuotesTransitColumn
              quote={selectedQuote}
              shipmentState={shipment.shipmentState}
              proNumber={undefined}
              billOfLadingNumber={undefined}
              broker={shipment.broker}
              showCarrierLogo={true}
              showPriceSection={true}
              price={discountedQuote.price}
              taxAmount={discountedQuote.taxAmount}
              currency={discountedQuote.currency}
              priceBeforeCoupon={
                (discountedQuote as QuoteWithOriginalPrice).priceBeforeCoupon
              }
              selectedCard={selectedCard}
              selectedBankAccount={selectedBankAccount}
              paymentTermsDays={paymentTermsDays}
              onChangePaymentMethod={props.onChangePaymentMethod}
              onChangeBroker={props.onChangeBroker}
              onChangeQuote={props.onSelectAnotherQuote}
              addInsuranceToShipment={shipment.addInsuranceToShipment}
              insuranceAmount={shipment.insuranceAmount}
              insuranceCurrency={shipment.insuranceCurrency}
              stateOfGoods={shipment.stateOfGoods}
              insurancePremiumAmount={shipment.insurancePremiumAmount}
              insurancePremiumCurrency={shipment.insurancePremiumCurrency}
              insurancePremiumTaxesAmount={shipment.insurancePremiumTaxesAmount}
              insurancePremiumReferralReason={
                shipment.insurancePremiumReferralReason
              }
              insuranceCommodityGroup={shipment.insuranceCommodityGroup}
            />
            <QuotesDeliveryColumn
              quote={selectedQuote}
              deliveryHours={deliveryHours}
              appointmentDate={undefined}
              deliveryLocation={shipment.deliveryLocation}
              deliveryContact={shipment.deliveryContact}
              shipmentState={shipment.shipmentState}
              actualDeliveryDate={undefined}
              actualDeliveryTime={undefined}
              expectedDeliveryDate={selectedQuote.expectedDeliveryDate}
              latestExpectedDeliveryDate={
                selectedQuote.latestEstimatedDeliveryDate
              }
              deliveryReferenceNumber={shipment.deliveryReferenceNumber}
              deliveryAmazonReferenceId={shipment.deliveryAmazonReferenceId}
              deliveryAmazonShipmentId={shipment.deliveryAmazonShipmentId}
              deliveryBoothNumber={shipment.deliveryBoothNumber}
              deliveryDeadline={shipment.deliveryDeadline}
              onModifyDetails={props.onModifyDetails}
              onModifyDeliveryAddress={props.onModifyDeliveryAddress}
              onModifyDeliveryContact={props.onModifyDeliveryAddress}
              onModifyDeliveryReferenceAndNotes={props.onModifyDeliveryAddress}
              showNotesSection={true}
              deliveryLocationHasDock={shipment.deliveryLocationHasDock}
              deliveryLocationHasForklift={shipment.deliveryLocationHasForklift}
            />
          </BookShipmentReviewSummaryColumns>
        </Stack>
      </Stack>
    </Panel>
  );
}
